"use client";

import { useEffect, useRef, useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { Button } from "./ui";
import type { WebsitePageAction } from "./WebsitePageDetails";
import css from "./BuildyWorkspace.module.css";

export function WebsitePageMenu({
  name,
  included,
  canSetVisibility,
  launched,
  disabled,
  onAction,
}: {
  name: string;
  included: boolean;
  canSetVisibility: boolean;
  launched: boolean;
  disabled: boolean;
  onAction: (action: WebsitePageAction) => void;
}) {
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  useEffect(() => {
    if (!position) return;
    menu.current?.querySelector<HTMLButtonElement>("button")?.focus();
    function outside(event: PointerEvent) {
      if (
        !menu.current?.contains(event.target as Node) &&
        !trigger.current?.contains(event.target as Node)
      )
        setPosition(null);
    }
    function close() {
      setPosition(null);
    }
    document.addEventListener("pointerdown", outside);
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [position]);
  function choose(action: WebsitePageAction) {
    setPosition(null);
    trigger.current?.focus();
    onAction(action);
  }
  return (
    <>
      <Button
        ref={trigger}
        className={css.pageMenuTrigger}
        variant="ghost"
        aria-label={`Actions for ${name}`}
        aria-haspopup="menu"
        aria-expanded={!!position}
        disabled={disabled}
        onClick={() => {
          if (position) {
            setPosition(null);
            return;
          }
          const rect = trigger.current!.getBoundingClientRect();
          setPosition({
            top: Math.max(
              8,
              Math.min(rect.bottom + 6, window.innerHeight - 100),
            ),
            left: Math.max(
              8,
              Math.min(rect.right - 184, window.innerWidth - 192),
            ),
          });
        }}
      >
        <MoreHorizontal size={16} />
      </Button>
      {position && (
        <div
          ref={menu}
          className={css.pageRowMenu}
          style={position}
          role="menu"
          aria-label={`Actions for ${name}`}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setPosition(null);
              trigger.current?.focus();
            }
            if (event.key === "Tab") setPosition(null);
            const items = [
              ...(menu.current?.querySelectorAll<HTMLButtonElement>("button") ??
                []),
            ];
            const current = items.indexOf(
              document.activeElement as HTMLButtonElement,
            );
            if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
              event.preventDefault();
              const next =
                event.key === "Home"
                  ? 0
                  : event.key === "End"
                    ? items.length - 1
                    : (current +
                        (event.key === "ArrowDown" ? 1 : -1) +
                        items.length) %
                      items.length;
              items[next]?.focus();
            }
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => choose("rename")}
          >
            Rename
          </button>
          {canSetVisibility && (
            <button
              type="button"
              role="menuitem"
              onClick={() => choose(included ? "unpublish" : "publish")}
            >
              {launched
                ? included
                  ? "Unpublish"
                  : "Publish"
                : included
                  ? "Exclude from website"
                  : "Include in website"}
            </button>
          )}
        </div>
      )}
    </>
  );
}
